<!--
    `closable`/`closeOnEscape` condicionais, como o diálogo irmão do quadro faz com `salvando()`: com
    uma chamada ao portal em voo, fechar recarregaria o quadro antes de o desfecho existir — o
    protocolo voltaria à listagem como aberto, e o analista o remarcaria num lote novo. Ver
    `fechamentoBloqueado` no componente.
-->
<p-dialog
    appDialogoPadrao="pi pi-ban"
    descricao="O encerramento vale para todos os marcados."
    header="Encerrar protocolos em lote"
    [visible]="visivel()"
    (visibleChange)="aoMudarVisibilidade($event)"
    [closable]="!fechamentoBloqueado()"
    [closeOnEscape]="!fechamentoBloqueado()"
    [style]="{ width: '58rem' }"
    [breakpoints]="{ '1024px': '92vw', '640px': '98vw' }"
>
    <!--
        A ação vem ANTES da lista, e não junto do botão de confirmar: efetivar e cancelar têm gates
        diferentes, então a mesma seleção produz listas diferentes. Ler a lista sem saber qual ação
        a produziu seria conferir a coisa errada. Depois do disparo ela vira texto: trocá-la ali
        recalcularia a conferência de um lote que já saiu.
    -->
    @if (!disparado()) {
        <!--
            Mesmo par de badges do diálogo de mover, e pelo mesmo motivo: esta é a pergunta mais cara
            da tela — efetivar e cancelar são IRREVERSÍVEIS no portal, e aqui valem para N protocolos
            de uma vez —, e ela vinha com o peso visual de dois radios miúdos numa linha.

            O `input[type=radio]` continua no documento, apenas fora da vista (ver SCSS): é ele que dá
            o grupo exclusivo, a navegação por setas e o anúncio do leitor de tela. O que virou badge é
            o RÓTULO. Trocar por dois botões perderia as três coisas.

            O modificador vai DIRETO no badge, e não por `input:checked +`: o shim de encapsulamento do
            Angular reescreve a cadeia de seletor e a regra deixa de casar — foi assim que a primeira
            versão do diálogo irmão saiu sem cor nenhuma no marcado.
        -->
        <fieldset class="lote-acao">
            <legend class="lote-acao-titulo">O que fazer com os protocolos selecionados</legend>
            <label class="lote-acao-opcao">
                <input type="radio" name="lote-acao" value="efetivar" [checked]="acao() === 'efetivar'" (change)="definirAcao('efetivar')" />
                <span [class]="acao() === 'efetivar' ? 'lote-acao-badge lote-acao-badge--efetivar' : 'lote-acao-badge'"><i class="pi pi-check-circle" aria-hidden="true"></i> Efetivar</span>
            </label>
            <label class="lote-acao-opcao">
                <input type="radio" name="lote-acao" value="cancelar" [checked]="acao() === 'cancelar'" (change)="definirAcao('cancelar')" />
                <span [class]="acao() === 'cancelar' ? 'lote-acao-badge lote-acao-badge--cancelar' : 'lote-acao-badge'"><i class="pi pi-times-circle" aria-hidden="true"></i> Cancelar</span>
            </label>
        </fieldset>

        @if (exigeMotivo()) {
            <label class="lote-rotulo" for="lote-motivo">Motivo do cancelamento</label>
            <textarea id="lote-motivo" pTextarea class="w-full mb-3" rows="3" maxlength="4000" [ngModel]="motivo()" (ngModelChange)="definirMotivo($event)" placeholder="O mesmo motivo vale para todos os protocolos do lote"></textarea>
        }
    }

    @if (colunaDaSelecao(); as coluna) {
        <!--
            A coluna aparece uma vez, e não por linha: a seleção é sempre de uma coluna só (o botão de
            encerrar em lote só existe na coluna dona dela), então repeti-la em N linhas seria ruído
            constante em vez do sinal que ela deveria dar.
        -->
        <p class="lote-origem">
            Coluna de origem: <strong>{{ coluna.status.nome }}</strong>
        </p>
    }

    @if (disparado()) {
        <!--
            `role="status"` porque este bloco muda sozinho, sem o analista tocar em nada: sem ele,
            quem usa leitor de tela ficaria sem saber que a fila andou.

            Os CINCO números são cinco, e não um só de "não deu certo": "com pendências" é um desfecho
            próprio (somá-lo aos encerrados esconderia o trabalho de cadastro que sobrou), e "sem
            movimento no quadro" é outro — o protocolo está finalizado no portal, e contá-lo em
            "falharam" mandaria refazer o que não pode ser refeito. O nome de cada um é o que a
            respectiva lista abaixo usa: "retirados do lote" é quem o analista tirou à mão, e não se
            confunde com os que o gate barrou, que têm seção própria mais abaixo.
        -->
        <div class="lote-andamento" role="status">
            <p class="lote-progresso">{{ progresso() }} protocolos processados</p>
            <ul class="lote-resumo">
                <li class="lote-estado-concluido">{{ encerrados() }} encerrados</li>
                <li class="lote-estado-com-pendencias">{{ resumo().comPendencias }} com pendências</li>
                <li class="lote-estado-falhou">{{ resumo().falharam }} falharam</li>
                <li class="lote-estado-preso">{{ resumo().presos }} sem movimento no quadro</li>
                <li class="lote-estado-fora">{{ resumo().foraDoLote }} retirados do lote</li>
            </ul>
            @if (filaParada()) {
                <!--
                    Parada por sessão expirada, parada a pedido do analista e parada porque a própria
                    orquestração quebrou são o MESMO estado para a fila e três coisas diferentes para
                    quem lê: numa ele já sabe o que houve; na segunda precisa entrar de novo antes de
                    qualquer Repetir valer alguma coisa; na terceira ninguém pediu nada, e é o único
                    caso em que a tela não sabe dizer se o item em voo chegou ao fim. Quem distingue é
                    o serviço (é ele que vê o 401 e o erro da orquestração), não uma dedução da tela.
                -->
                @if (sessaoExpirada()) {
                    <p class="lote-aviso">A sessão expirou e o lote parou aí. Entre no sistema de novo antes de usar Repetir — o que já havia sido encerrado continua encerrado.</p>
                } @else if (falhaInesperadaNaFila()) {
                    <p class="lote-aviso">O lote parou por uma falha inesperada, e não por pedido de ninguém. Confira o estado de cada protocolo abaixo antes de usar Repetir — o que já havia sido encerrado continua encerrado.</p>
                } @else {
                    <p class="lote-aviso">O lote foi interrompido. O que já havia sido encerrado continua encerrado.</p>
                }
            }
            @if (filaEncerrada()) {
                <p class="lote-aviso">A fila terminou. Confira as pendências e as falhas antes de fechar — esta lista não volta.</p>
            }
            @if (linhasPresas().length > 0) {
                <!--
                    O aviso mais caro da tela, e por isso `role="alert"`: estes protocolos estão
                    finalizados no portal e parados no quadro. Repetir aqui grava SÓ o movimento (a fila
                    lembra que o portal já aceitou). Fechar sem repetir tira o card da tela — ele sai da
                    listagem viva e não entra na coluna Concluído —, e a recuperação passa a ser pela
                    tela de detalhe do protocolo.
                -->
                <p class="lote-aviso lote-aviso-preso" role="alert">
                    {{ linhasPresas().length }} {{ linhasPresas().length === 1 ? 'protocolo foi finalizado' : 'protocolos foram finalizados' }} no portal sem o movimento ser gravado no quadro. Use Repetir em cada um: a repetição grava só o movimento,
                    sem refazer a finalização. Se fechar assim, {{ linhasPresas().length === 1 ? 'o card some' : 'os cards somem' }} do quadro e a recuperação passa a ser pela tela de detalhe do protocolo.
                </p>
            }
        </div>
    }

    <h3 class="lote-secao-titulo">No lote ({{ linhas().length }})</h3>
    @if (linhas().length === 0) {
        @if (!disparado()) {
            <p class="lote-vazio">Nenhum dos protocolos selecionados pode ser {{ exigeMotivo() ? 'cancelado' : 'efetivado' }} em lote.</p>
        } @else {
            <p class="lote-vazio">Nenhum protocolo restou no lote.</p>
        }
    } @else {
        <table class="lote-tabela">
            <caption class="sr-only">
                Protocolos do lote e o estado de cada um
            </caption>
            <thead>
                <tr>
                    <!--
                        Protocolo, nome e CRM numa coluna só, ao lado do retrato: os quatro respondem
                        "quem é este protocolo", e é assim que o analista acabou de vê-los no card do
                        quadro. Separados em duas colunas, ele reconstruía a identidade lendo a linha
                        da esquerda para a direita a cada vez.
                    -->
                    <th scope="col">Protocolo</th>
                    <!--
                        `ItemStatus` não carrega a data de abertura do protocolo; a única data que ele
                        traz é a da última tramitação. O rótulo diz o que o dado É — chamá-la de
                        "Aberto em" faria a tela mentir sobre a idade do protocolo.
                    -->
                    <th scope="col">Última tramitação</th>
                    @if (disparado()) {
                        <th scope="col">Estado</th>
                    }
                    <th scope="col"><span class="sr-only">Ações do protocolo no lote</span></th>
                </tr>
            </thead>
            <tbody>
                <!--
                    A MESMA lista das duas fases, nas mesmas posições: depois do disparo ela só ganha a
                    coluna de estado. Trocar de tela obrigaria o analista a reencontrar cada protocolo.
                -->
                @for (linha of linhas(); track linha.chave) {
                    <tr>
                        <td>
                            <!--
                                O mesmo bloco do card e do diálogo de mover: retrato à esquerda e, ao
                                lado, protocolo, nome e identificação empilhados.
                            -->
                            <span class="lote-identificacao">
                                @if (fotoDoRetratado(linha.item); as foto) {
                                    <!--
                                        `alt` vazio e `aria-hidden` quando o retrato é decorativo: na
                                        pessoa física ele mostra exatamente o nome que se lê ao lado, e
                                        anunciá-lo repetiria a mesma informação. Na jurídica o rosto é
                                        de OUTRA pessoa (o diretor técnico), e aí ganha rótulo próprio.
                                    -->
                                    <img class="lote-avatar" [src]="foto" [alt]="rotuloDoRetrato(linha.item)" [attr.aria-hidden]="rotuloDoRetrato(linha.item) ? null : true" [title]="rotuloDoRetrato(linha.item) || null" />
                                } @else if (iniciaisDoRetratado(linha.item); as iniciais) {
                                    <span
                                        class="lote-avatar lote-avatar--iniciais"
                                        [attr.aria-label]="rotuloDoRetrato(linha.item) || null"
                                        [attr.aria-hidden]="rotuloDoRetrato(linha.item) ? null : true"
                                        [title]="rotuloDoRetrato(linha.item) || null"
                                        >{{ iniciais }}</span
                                    >
                                }
                                <span class="lote-nomes">
                                    <span class="lote-badge lote-protocolo">#{{ identificador(linha.item) }}</span>
                                    <strong class="lote-solicitante">{{ linha.item.empresa }}</strong>
                                    @if (identificacaoDoSolicitante(linha.item); as identificacao) {
                                        <span class="lote-documento">{{ identificacao }}</span>
                                    }
                                </span>
                            </span>
                        </td>
                        <td>{{ ultimaTramitacao(linha.item) }}</td>
                        @if (disparado()) {
                            <td class="lote-estado">
                                <span [class]="linha.classe"><i [class]="linha.icone" aria-hidden="true"></i>{{ linha.rotulo }}</span>
                                @if (linha.pendencias.length > 0) {
                                    <!-- QUAL campo faltou, não "há pendências": é o campo que diz o que corrigir. -->
                                    <span class="lote-estado-detalhe">Faltou: {{ linha.pendencias.join('; ') }}</span>
                                }
                                @if (linha.erro) {
                                    <!-- O erro real daquele item, nunca uma frase genérica: é ele que diz se adianta repetir. -->
                                    <span class="lote-estado-detalhe">{{ linha.erro }}</span>
                                }
                            </td>
                        }
                        <td class="lote-acoes-linha">
                            @if (podeRepetir(linha.chave)) {
                                <button pButton type="button" icon="pi pi-replay" class="p-button-text p-button-sm" [attr.aria-label]="'Repetir o protocolo ' + identificador(linha.item)" (click)="repetir(linha.chave)"></button>
                            }
                            @if (linha.removivel) {
                                <button pButton type="button" icon="pi pi-times" class="p-button-text p-button-danger p-button-sm" [attr.aria-label]="'Remover do lote o protocolo ' + identificador(linha.item)" (click)="remover(linha.chave)"></button>
                            }
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    }

    @if (linhasComPendencias().length > 0 || linhasQueFalharam().length > 0 || linhasPresas().length > 0) {
        <!--
            O que sobrevive ao fechamento é esta lista — é dela que sai o trabalho de correção. Por
            isso ela é copiável como texto: quem encerrou quarenta protocolos e teve três falhas vai
            querer anotar quais, e as pendências viram a fila de cadastro do dia seguinte.
        -->
        <div class="lote-copia">
            <button pButton type="button" icon="pi pi-copy" label="Copiar resultado" class="p-button-text p-button-sm" (click)="copiarResultado()"></button>
            @if (mensagemDaCopia()) {
                <span class="lote-copia-aviso" role="status">{{ mensagemDaCopia() }}</span>
            }
        </div>
    }

    @if (foraDoLote().length > 0) {
        <!--
            "Não entraram no lote", e não "Fora do lote": este número é o dos barrados pelo gate de
            elegibilidade, e o rodapé de andamento tem OUTRO chamado "retirados do lote" (os que o
            analista tirou à mão). Com o mesmo nome nos dois, quem lia "0 fora do lote" no resumo
            depois de ver 12 barrados aqui concluía que a tela estava errada em algum dos dois.
        -->
        <h3 class="lote-secao-titulo">Não entraram no lote ({{ foraDoLote().length }})</h3>
        @if (agregadosForaDoLote().length > 0) {
            <ul class="lote-agregados">
                @for (agregado of agregadosForaDoLote(); track agregado.motivo) {
                    <li>{{ textoDoAgregado(agregado) }}</li>
                }
            </ul>
        }
        <ul class="lote-fora">
            @for (avaliado of foraDoLote(); track avaliado.chave) {
                <li>
                    <span class="lote-protocolo">#{{ identificador(avaliado.item) }}</span>
                    <span class="lote-fora-solicitante">{{ avaliado.item.empresa }}</span>
                    @if (avaliado.motivo; as motivoDoItem) {
                        <span class="lote-fora-motivo">{{ textoDoMotivo(motivoDoItem) }}</span>
                    }
                </li>
            }
        </ul>
    }

    <!--
        Parar fica no rodapé e some quando não há mais o que parar. Fechar nunca desaparece, e o
        diálogo NUNCA se fecha sozinho ao terminar: o resumo e a lista de pendências são o produto do
        lote, e fechá-los por conta própria levaria embora o trabalho de correção.
    -->
    <ng-template pTemplate="footer">
        @if (fechamentoBloqueado()) {
            <!--
                Botão desabilitado sem explicação vira chamado de suporte. `role="status"` porque o
                bloqueio aparece e some sozinho, ao ritmo da fila: quem usa leitor de tela precisa
                saber por que Fechar deixou de responder — e que a espera acaba sozinha.
            -->
            <span class="lote-aviso lote-fechar-bloqueado" role="status">Aguarde a chamada em andamento terminar para fechar — fechar agora recarregaria o quadro antes de o portal responder.</span>
        }
        @if (podeParar()) {
            <button pButton type="button" label="Parar" severity="danger" class="p-button-text" (click)="parar()"></button>
        }
        <button pButton type="button" label="Fechar" class="p-button-text" [disabled]="fechamentoBloqueado()" (click)="fechar()"></button>
        @if (!disparado()) {
            <button pButton type="button" [label]="rotuloDoConfirmar()" [disabled]="!podeConfirmar()" (click)="confirmar()"></button>
        }
    </ng-template>
</p-dialog>
